import {
    BOX_TRUCK_DAT_SELECTION_VERSION,
    BOX_TRUCK_EQUIPMENT_LABELS,
    type BoxTruckCapacityLimits,
    BoxTruckLocationSource,
    migrateBoxTruckEquipmentLabel,
    sanitizeBoxTruckCapacityLimits,
    restoreBoxTruckDatSubtypeCodes,
    sanitizeBoxTruckDatSubtypeCodes,
    sanitizeBoxTruckMaxPostedAgeHours,
} from '@numeo/types-core'
import { LocationData } from 'features/load-hub/types/filters'
import { BoxTruckRefinements } from '../components/BoxTruckRefinementBar'
import { sanitizeHiddenSources, sanitizeRefinements } from './refinementSerialization'
import { BoxTruckSearchFilters, capOrNull, EMPTY_FILTERS, loadTypeOrBoth, sanitizePickup, sanitizePickupEnd } from '../hooks/requestMapping'
import { BoxTruckVerification } from '../services/boxTruckLoadsApi'

/** URL parameter carrying only an opaque, same-origin storage key — search details never enter the URL. */
export const BOX_TRUCK_BROWSER_TAB_PARAM = 'openSearch'
export const BOX_TRUCK_FRESH_BROWSER_TAB_SENTINEL = 'fresh'

const STORAGE_PREFIX = 'box_truck_browser_handoff:'
const VERSION = 1
const TTL_MS = 10 * 60 * 1000
const MAX_PAYLOAD_BYTES = 96 * 1024
const TOKEN_PATTERN = /^[a-f0-9]{32}$/
const MAX_SELECTED_DRIVERS = 50
const MAX_STORED_HANDOFFS = 25
/** Allowlisted so a handoff payload cannot smuggle an arbitrary string into the Last Location icon lookup. */
const LOCATION_SOURCES: readonly BoxTruckLocationSource[] = ['mobile', 'admin', 'telegram', 'sms']

interface SerializedFilters {
    origin: LocationData | null
    destination: LocationData | null
    originOptions: LocationData[]
    destinationOptions: LocationData[]
    originRadius: number | null
    destinationRadius: number | null
    startDate: string | null
    endDate: string | null
    equipmentLabels: string[]
    datEquipmentTypes: string[]
    datEquipmentSelectionVersion: number
    capacityLimits: BoxTruckCapacityLimits
    includeUnknownCapacity: boolean
    fullPartial: string
    lengthFt: number | null
    weightLbs: number | null
    maxPostedAgeHours: number
}

interface StoredBrowserTabHandoff {
    version: typeof VERSION
    createdAt: number
    sourceUserId: string
    sourceApplicationId: string
    tab: {
        filters: SerializedFilters
        searched: boolean
        label?: string
    }
    draftFilters: SerializedFilters
    query: string
    refinements: BoxTruckRefinements
    hiddenSources: string[]
    selectedDrivers: BoxTruckVerification[]
    selectedLoadKey: string | null
}

export interface BoxTruckBrowserTabWorkspace {
    sourceUserId: string
    sourceApplicationId: string
    tab: {
        filters: BoxTruckSearchFilters
        searched: boolean
        label?: string
    }
    /** Exact values visible in the filter controls, including edits not submitted yet. */
    draftFilters: BoxTruckSearchFilters
    query: string
    refinements: BoxTruckRefinements
    hiddenSources: string[]
    /** Deliberately scrubbed driver summaries; banking, documents and verification evidence are never copied. */
    selectedDrivers: BoxTruckVerification[]
    selectedLoadKey: string | null
}

type BrowserTabHandoffReadResult = { status: 'valid'; workspace: BoxTruckBrowserTabWorkspace } | { status: 'missing' | 'expired' | 'invalid' }

const asRecord = (value: unknown): Record<string, unknown> | null =>
    typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null

const boundedString = (value: unknown, max: number): string | null => (typeof value === 'string' && value.length <= max ? value : null)

const optionalString = (value: unknown, max: number): string | undefined => {
    const string = boundedString(value, max)?.trim()
    return string || undefined
}

const finiteNumber = (value: unknown, min: number, max: number): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max ? value : undefined

const sanitizeLocation = (value: unknown): LocationData | null => {
    if (value === null || value === undefined) return null
    const record = asRecord(value)
    if (!record) return null
    const latitude = finiteNumber(record.latitude, -90, 90)
    const longitude = finiteNumber(record.longitude, -180, 180)
    return {
        ...(optionalString(record.city, 120) ? { city: optionalString(record.city, 120) } : {}),
        ...(optionalString(record.stateProv, 20) ? { stateProv: optionalString(record.stateProv, 20) } : {}),
        ...(optionalString(record.postalCode, 24) ? { postalCode: optionalString(record.postalCode, 24) } : {}),
        ...(latitude !== undefined ? { latitude } : {}),
        ...(longitude !== undefined ? { longitude } : {}),
        ...(optionalString(record.zoneCode, 20) ? { zoneCode: optionalString(record.zoneCode, 20) } : {}),
        ...(optionalString(record.zoneStates, 500) ? { zoneStates: optionalString(record.zoneStates, 500) } : {}),
    }
}

const sanitizeLocations = (value: unknown): LocationData[] =>
    Array.isArray(value)
        ? value
              .slice(0, 60)
              .map(sanitizeLocation)
              .filter((location): location is LocationData => location !== null)
        : []

const serializeFilters = (filters: BoxTruckSearchFilters): SerializedFilters => ({
    origin: sanitizeLocation(filters.origin),
    destination: sanitizeLocation(filters.destination),
    originOptions: sanitizeLocations(filters.originOptions),
    destinationOptions: sanitizeLocations(filters.destinationOptions),
    originRadius: capOrNull(filters.originRadius),
    destinationRadius: capOrNull(filters.destinationRadius),
    startDate: filters.startDate instanceof Date && Number.isFinite(filters.startDate.getTime()) ? filters.startDate.toISOString() : null,
    endDate: filters.endDate instanceof Date && Number.isFinite(filters.endDate.getTime()) ? filters.endDate.toISOString() : null,
    // Migrate BEFORE the membership test. Dropping an unrecognised label is not a safe default here: the
    // detaching tab opens with NO equipment filter rather than the one the operator had, which widens the
    // search silently instead of showing a stale chip they could fix.
    equipmentLabels: (filters.equipmentLabels ?? [])
        .map(migrateBoxTruckEquipmentLabel)
        .filter((label): label is string => BOX_TRUCK_EQUIPMENT_LABELS.includes(label))
        .slice(0, 20),
    datEquipmentTypes: sanitizeBoxTruckDatSubtypeCodes(filters.datEquipmentTypes),
    datEquipmentSelectionVersion: BOX_TRUCK_DAT_SELECTION_VERSION,
    capacityLimits: sanitizeBoxTruckCapacityLimits(filters.capacityLimits),
    includeUnknownCapacity: filters.includeUnknownCapacity !== false,
    fullPartial: loadTypeOrBoth(filters.fullPartial),
    lengthFt: capOrNull(filters.lengthFt),
    weightLbs: capOrNull(filters.weightLbs),
    maxPostedAgeHours: sanitizeBoxTruckMaxPostedAgeHours(filters.maxPostedAgeHours),
})

const parseDate = (value: unknown): Date | null => {
    if (typeof value !== 'string') return null
    const date = new Date(value)
    return Number.isFinite(date.getTime()) ? date : null
}

const deserializeFilters = (value: unknown, preserveEmptyDates = false): BoxTruckSearchFilters => {
    const record = asRecord(value) ?? {}
    const origin = sanitizeLocation(record.origin)
    const destination = sanitizeLocation(record.destination)
    const originOptions = sanitizeLocations(record.originOptions)
    const destinationOptions = sanitizeLocations(record.destinationOptions)
    const startDate = parseDate(record.startDate)
    const endDate = parseDate(record.endDate)
    return {
        ...EMPTY_FILTERS,
        origin,
        destination,
        originOptions: originOptions.length ? originOptions : origin ? [origin] : [],
        destinationOptions: destinationOptions.length ? destinationOptions : destination ? [destination] : [],
        originRadius: capOrNull(record.originRadius),
        destinationRadius: capOrNull(record.destinationRadius),
        startDate: preserveEmptyDates && record.startDate === null ? null : sanitizePickup(startDate),
        endDate: preserveEmptyDates && record.endDate === null ? null : sanitizePickupEnd(endDate),
        equipmentLabels: Array.isArray(record.equipmentLabels)
            ? record.equipmentLabels
                  .filter((label): label is string => typeof label === 'string')
                  .map(migrateBoxTruckEquipmentLabel)
                  .filter((label) => BOX_TRUCK_EQUIPMENT_LABELS.includes(label))
                  .slice(0, 20)
            : [],
        // `restore`, not `sanitize`: a handoff written before this contract carries no field at all, and
        // reading that blank as a deliberate clear switches the detached tab's DAT leg off while its
        // toggle still reads on. The tab and localStorage paths already restore this way.
        datEquipmentTypes: restoreBoxTruckDatSubtypeCodes(
            record.datEquipmentTypes,
            typeof record.datEquipmentSelectionVersion === 'number' ? record.datEquipmentSelectionVersion : undefined
        ),
        capacityLimits: sanitizeBoxTruckCapacityLimits(record.capacityLimits),
        includeUnknownCapacity: record.includeUnknownCapacity !== false,
        fullPartial: loadTypeOrBoth(record.fullPartial),
        lengthFt: capOrNull(record.lengthFt),
        weightLbs: capOrNull(record.weightLbs),
        maxPostedAgeHours: sanitizeBoxTruckMaxPostedAgeHours(record.maxPostedAgeHours),
        numeoId: null,
    }
}

/**
 * Reduce a selected driver to the fields needed by the picker and Nearby Drivers pin. In particular, never
 * copy registration documents, bank numbers, licence data, carrier-owner data, Telegram data or HR metadata.
 */
const sanitizeDriver = (value: unknown): BoxTruckVerification | null => {
    const record = asRecord(value)
    const boxTruckInfo = asRecord(record?.boxTruckInfo)
    const info = asRecord(boxTruckInfo?.info)
    const userId = boundedString(record?.userId, 100)?.trim()
    const claimDriverId = boundedString(record?.claimDriverId, 100)?.trim()
    if (!record || !boxTruckInfo || !userId) return null

    const status = boxTruckInfo.status === 'pending' || boxTruckInfo.status === 'active' || boxTruckInfo.status === 'inactive' ? boxTruckInfo.status : undefined
    const sanitizedInfo = {
        ...(optionalString(info?.name, 160) ? { name: optionalString(info?.name, 160) } : {}),
        ...(optionalString(info?.phone, 60) ? { phone: optionalString(info?.phone, 60) } : {}),
        ...(optionalString(info?.equipmentType, 100) ? { equipmentType: optionalString(info?.equipmentType, 100) } : {}),
        ...(optionalString(info?.truckNumber, 80) ? { truckNumber: optionalString(info?.truckNumber, 80) } : {}),
        ...(optionalString(info?.cargoDimensions, 100) ? { cargoDimensions: optionalString(info?.cargoDimensions, 100) } : {}),
        ...(optionalString(info?.emergencyContactPhone, 60) ? { emergencyContactPhone: optionalString(info?.emergencyContactPhone, 60) } : {}),
        ...(finiteNumber(info?.maxCargoWeightLbs, 0, 1_000_000) !== undefined
            ? { maxCargoWeightLbs: finiteNumber(info?.maxCargoWeightLbs, 0, 1_000_000) }
            : {}),
    }
    const lastLocationRecord = asRecord(record.lastLocation)
    const lat = finiteNumber(lastLocationRecord?.lat, -90, 90)
    const lng = finiteNumber(lastLocationRecord?.lng, -180, 180)
    const fixAt = boundedString(lastLocationRecord?.fixAt, 100)
    const source = LOCATION_SOURCES.find((candidate) => candidate === lastLocationRecord?.source)
    const lastLocation =
        lat !== undefined && lng !== undefined && fixAt
            ? {
                  lat,
                  lng,
                  fixAt,
                  ...(optionalString(lastLocationRecord?.city, 120) ? { city: optionalString(lastLocationRecord?.city, 120) } : {}),
                  ...(optionalString(lastLocationRecord?.state, 20) ? { state: optionalString(lastLocationRecord?.state, 20) } : {}),
                  ...(source ? { source } : {}),
              }
            : undefined
    const profileLocationRecord = asRecord(record.profileLocation)
    const profileLocation = profileLocationRecord
        ? {
              ...(optionalString(profileLocationRecord.city, 120) ? { city: optionalString(profileLocationRecord.city, 120) } : {}),
              ...(optionalString(profileLocationRecord.state, 20) ? { state: optionalString(profileLocationRecord.state, 20) } : {}),
          }
        : undefined

    return {
        userId,
        ...(claimDriverId ? { claimDriverId } : {}),
        email: boundedString(record.email, 320) ?? '',
        name: boundedString(record.name, 160) ?? '',
        boxTruckInfo: {
            enabled: boxTruckInfo.enabled === true,
            ...(status ? { status } : {}),
            info: sanitizedInfo,
        },
        ...(lastLocation ? { lastLocation } : {}),
        ...(profileLocation ? { profileLocation } : {}),
    }
}

export const sanitizeBoxTruckSelectedDrivers = (value: unknown): BoxTruckVerification[] => {
    if (!Array.isArray(value)) return []
    const byId = new Map<string, BoxTruckVerification>()
    for (const raw of value.slice(0, MAX_SELECTED_DRIVERS)) {
        const driver = sanitizeDriver(raw)
        if (driver) byId.set(driver.userId, driver)
    }
    return [...byId.values()]
}

const randomToken = (): string | null => {
    try {
        const bytes = new Uint8Array(16)
        crypto.getRandomValues(bytes)
        return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
    } catch {
        return null
    }
}

const storageKey = (token: string): string => `${STORAGE_PREFIX}${token}`

/** Opportunistically expire abandoned handoffs and cap rapid repeats. Read-time expiry remains authoritative. */
const pruneExpired = (now: number): void => {
    try {
        const live: { key: string; createdAt: number }[] = []
        for (let index = localStorage.length - 1; index >= 0; index -= 1) {
            const key = localStorage.key(index)
            if (!key?.startsWith(STORAGE_PREFIX)) continue
            const raw = localStorage.getItem(key)
            if (!raw || raw.length > MAX_PAYLOAD_BYTES) {
                localStorage.removeItem(key)
                continue
            }
            try {
                const record = asRecord(JSON.parse(raw))
                if (
                    typeof record?.createdAt !== 'number' ||
                    !Number.isFinite(record.createdAt) ||
                    record.createdAt > now + 60_000 ||
                    now - record.createdAt > TTL_MS
                ) {
                    localStorage.removeItem(key)
                    continue
                }
                live.push({ key, createdAt: record.createdAt })
            } catch {
                localStorage.removeItem(key)
            }
        }
        // Leave one slot for the handoff about to be written. This bounds storage even if a user repeatedly
        // taps the action faster than the ten-minute expiry window.
        live.sort((left, right) => right.createdAt - left.createdAt)
        live.slice(MAX_STORED_HANDOFFS - 1).forEach(({ key }) => localStorage.removeItem(key))
    } catch {
        // Storage can be blocked. A handoff still expires when read.
    }
}

export const createBoxTruckBrowserTabHandoff = (workspace: BoxTruckBrowserTabWorkspace): string | null => {
    const sourceUserId = boundedString(workspace.sourceUserId, 100)?.trim()
    const sourceApplicationId = boundedString(workspace.sourceApplicationId, 100)?.trim()
    const token = randomToken()
    if (!sourceUserId || !sourceApplicationId || !token) return null

    const now = Date.now()
    const payload: StoredBrowserTabHandoff = {
        version: VERSION,
        createdAt: now,
        sourceUserId,
        sourceApplicationId,
        tab: {
            filters: serializeFilters(workspace.tab.filters),
            searched: workspace.tab.searched === true,
            ...(optionalString(workspace.tab.label, 80) ? { label: optionalString(workspace.tab.label, 80) } : {}),
        },
        draftFilters: serializeFilters(workspace.draftFilters),
        query: boundedString(workspace.query, 200) ?? '',
        refinements: sanitizeRefinements(workspace.refinements),
        hiddenSources: sanitizeHiddenSources(workspace.hiddenSources),
        selectedDrivers: sanitizeBoxTruckSelectedDrivers(workspace.selectedDrivers),
        selectedLoadKey: boundedString(workspace.selectedLoadKey, 256),
    }

    try {
        pruneExpired(now)
        const serialized = JSON.stringify(payload)
        if (serialized.length > MAX_PAYLOAD_BYTES) return null
        localStorage.setItem(storageKey(token), serialized)
        return token
    } catch {
        return null
    }
}

export const readBoxTruckBrowserTabHandoff = (token: string, now = Date.now()): BrowserTabHandoffReadResult => {
    if (!TOKEN_PATTERN.test(token)) return { status: 'invalid' }
    try {
        const raw = localStorage.getItem(storageKey(token))
        if (!raw) return { status: 'missing' }
        if (raw.length > MAX_PAYLOAD_BYTES) return { status: 'invalid' }
        const record = asRecord(JSON.parse(raw))
        if (!record || record.version !== VERSION || typeof record.createdAt !== 'number' || !Number.isFinite(record.createdAt)) return { status: 'invalid' }
        // Reject timestamps too far in the future as well as stale payloads; either indicates tampering/bad data.
        if (record.createdAt > now + 60_000 || now - record.createdAt > TTL_MS) return { status: 'expired' }
        const sourceUserId = boundedString(record.sourceUserId, 100)?.trim()
        const sourceApplicationId = boundedString(record.sourceApplicationId, 100)?.trim()
        const tab = asRecord(record.tab)
        if (!sourceUserId || !sourceApplicationId || !tab || typeof tab.searched !== 'boolean') return { status: 'invalid' }

        return {
            status: 'valid',
            workspace: {
                sourceUserId,
                sourceApplicationId,
                tab: {
                    filters: deserializeFilters(tab.filters),
                    searched: tab.searched,
                    ...(optionalString(tab.label, 80) ? { label: optionalString(tab.label, 80) } : {}),
                },
                draftFilters: deserializeFilters(record.draftFilters, true),
                query: boundedString(record.query, 200) ?? '',
                refinements: sanitizeRefinements(record.refinements),
                hiddenSources: sanitizeHiddenSources(record.hiddenSources),
                selectedDrivers: sanitizeBoxTruckSelectedDrivers(record.selectedDrivers),
                selectedLoadKey: boundedString(record.selectedLoadKey, 256),
            },
        }
    } catch {
        return { status: 'invalid' }
    }
}

/** Remove a settled one-time handoff. Token validation prevents this helper from becoming an arbitrary
 *  localStorage deletion primitive if a malformed URL reaches the page. */
export const removeBoxTruckBrowserTabHandoff = (token: string): void => {
    if (!TOKEN_PATTERN.test(token)) return
    try {
        localStorage.removeItem(storageKey(token))
    } catch {
        // Storage disabled — the read path still enforces the TTL if the record remains accessible later.
    }
}

/** Physically remove an unconsumed record once its logical TTL ends. A blocked popup has no target page to
 *  consume the handoff, so read-time expiry alone would otherwise leave its private driver summary stored
 *  until another handoff happened to trigger opportunistic pruning. */
export const scheduleBoxTruckBrowserTabHandoffCleanup = (token: string): void => {
    if (!TOKEN_PATTERN.test(token)) return
    window.setTimeout(() => removeBoxTruckBrowserTabHandoff(token), TTL_MS)
}
